import { type Components, createTheme, type Theme } from '@mui/material/styles';

import { BREAKPOINTS } from '@/hooks/useMeasure';
import { BUTTONS } from './buttons';
import { CHOICES } from './choices';
import { DISPLAY } from './display';
import { FEEDBACK } from './feedback';
import { FIELDS } from './fields';
import { LISTS } from './lists';
import { MEDIA } from './media';
import { NAVIGATION } from './navigation';
import { OVERLAYS } from './overlays';
import { PALETTES, SHAPE, token, TYPOGRAPHY } from './scales';
import { SURFACES } from './surfaces';
import { UPLOADS } from './uploads';

/** The Material 3 look for every MUI component the kit wraps. */
const COMPONENTS: Components<Theme> = {
  ...BUTTONS,
  ...FIELDS,
  ...CHOICES,
  ...UPLOADS,
  ...SURFACES,
  ...LISTS,
  ...OVERLAYS,
  ...FEEDBACK,
  ...NAVIGATION,
  ...DISPLAY,
  ...MEDIA,
};

/** Returns the page-wide styles: the body, the keyboard focus ring and the scrollbars. */
export function globalStyles(theme: Theme) {
  return {
    body: {
      fontFamily: 'var(--font-sans)',
      color: theme.vars.palette.text.primary,
      backgroundColor: theme.vars.palette.background.default,
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
    },
    ':focus-visible': {
      outline: 'none',
      boxShadow: `0 0 0 3px ${token('brand-600', 0.2)}`,
    },
    '::-webkit-scrollbar': { width: 8, height: 8 },
    '::-webkit-scrollbar-track': { background: 'transparent' },
    '::-webkit-scrollbar-thumb': { background: theme.vars.palette.grey[300], borderRadius: SHAPE.full },
    '::-webkit-scrollbar-thumb:hover': { background: theme.vars.palette.grey[400] },
  };
}

/** The app theme; `.dark` on the root selects the dark scheme. */
export const THEME = createTheme({
  cssVariables: { colorSchemeSelector: 'class', nativeColor: true },
  colorSchemes: {
    light: { palette: PALETTES.light },
    dark: { palette: PALETTES.dark },
  },
  breakpoints: {
    values: { xs: 0, sm: BREAKPOINTS.sm, md: BREAKPOINTS.md, lg: BREAKPOINTS.lg, xl: BREAKPOINTS.xl },
  },
  shape: { borderRadius: SHAPE.small },
  motion: { reducedMotion: 'system' },
  typography: TYPOGRAPHY,
  components: COMPONENTS,
});
